import { createListenerMiddleware, addListener, ListenerEffectAPI, Action, isAnyOf } from '@reduxjs/toolkit'
import type { TypedStartListening, TypedAddListener } from '@reduxjs/toolkit'
import type { RootState, AppDispatch } from '.'
import { setVehicleGroup } from './features/resVehicleSlice'
import { ApprovalReq } from './features/types'
import { fetchRentalTotal, fetchResApproval } from './features/thunks'
import { selectApprovalProps } from './derivedSelectors'
import { updateReasonForHire, updateResEditAddress, updateTarrifBandId } from './features/resEditFormSlice'
import { updateAddress } from './features/resLocationsSlice'
import { updateBusiness } from './features/contextDropDownsSlice'

export const listenerMiddleware = createListenerMiddleware()
export type AppStartListening = TypedStartListening<RootState, AppDispatch>
export const startAppListening = listenerMiddleware.startListening as AppStartListening
export const addAppListener = addListener as TypedAddListener<RootState, AppDispatch>
export type ListenerFn<T> = (action: Action & { payload: T }, listenerApi: ListenerEffectAPI<RootState, AppDispatch, unknown>) => void

/* approval listener */
export const vehicleListener = (startListening: AppStartListening) => {
	startListening({
		actionCreator: setVehicleGroup,
		effect: async (action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const { numDays, businessId, driverId } = selectApprovalProps(listenerApi.getState())
			let request: ApprovalReq = {
				fieldType: 'RentalVehicleGroup',
				fieldValue: action.payload.id,
				businessId,
				rentalReservationId: null,
				alreadySetAddresses: false,
				driverId,
				numDays,
			}
			listenerApi.dispatch(fetchResApproval(request))
		},
	})
}
vehicleListener(startAppListening)

export const fetchRentalTotalListener = (startListening: AppStartListening) => {
	startListening({
		predicate: (_action, currentState, previousState) => {
			const state: { numDays: number; tariffBandId: string; vehicleGroupId: string } = selectApprovalProps(currentState)
			const prev = selectApprovalProps(previousState)
			const { numDays, tariffBandId, vehicleGroupId } = state

			/* 
			? have any of these values changed?  
			*/
			return (
				numDays &&
				tariffBandId &&
				vehicleGroupId &&
				(numDays !== prev.numDays || tariffBandId !== prev.tariffBandId || vehicleGroupId !== prev.vehicleGroupId)
			)
		},
		effect: async (_action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const { numDays, tariffBandId, vehicleGroupId } = selectApprovalProps(listenerApi.getState())
			/*
			 * fetches rental-price predicated on
			 * numDays,tarrifBandId and vehicleGroupId
			 * having changed or being initialialised
			 */
			listenerApi.dispatch(fetchRentalTotal({ tariffBandId: tariffBandId, numDays, vehicleGroupId }))
		},
	})
}
fetchRentalTotalListener(startAppListening)

/* 
! approval listeners
*/
export const reasonForHireListener = (startListening: AppStartListening) => {
	startListening({
		actionCreator: updateReasonForHire,
		effect: async (action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const { numDays, driverId, businessId } = selectApprovalProps(listenerApi.getState())
			let request: ApprovalReq = {
				fieldType: 'ReasonForHire',
				fieldValue: action.payload,
				businessId,
				rentalReservationId: null,
				alreadySetAddresses: false,
				driverId,
				numDays,
			}
			listenerApi.dispatch(fetchResApproval(request))
		},
	})
}
reasonForHireListener(startAppListening)

export const tarrifBandIdListener = (startListening: AppStartListening) => {
	startListening({
		matcher: isAnyOf(updateTarrifBandId),
		effect: async (action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const { tariffBandId, numDays, driverId, businessId, insuranceType, vehicleGroupId } = selectApprovalProps(
				listenerApi.getState()
			)
			let request: ApprovalReq = {
				fieldValue: action.payload,
				fieldType: 'TariffBand',
				businessId,
				rentalReservationId: null,
				alreadySetAddresses: false,
				driverId,
				numDays,
			}
			/* tariffBand request */
			listenerApi.dispatch(fetchResApproval(request))
			/* DailyRate request */
			const dailyRateRequest: ApprovalReq = { ...request, tariffBandId, insuranceType, vehicleGroupId, fieldType: 'DailyRate' }
			listenerApi.dispatch(fetchResApproval(dailyRateRequest))
			/* RentalValue request */
			const rentalVehalueRequest: ApprovalReq = { ...request, vehicleGroupId, insuranceType, fieldType: 'RentalValue' }
			listenerApi.dispatch(fetchResApproval(rentalVehalueRequest))
		},
	})
}
tarrifBandIdListener(startAppListening)

/* Listeners */

export const addressListener = (startListening: AppStartListening) => {
	startListening({
		actionCreator: updateAddress,
		effect: async (action, listenerApi) => {
			const {
				type,
				addressType,
				id,
				address,
				latLong,
			}: { type: 'collection' | 'delivery'; addressType: string; id: string; address: { [key: string]: string }; latLong: string } =
				action.payload
			const { sameAsStart } = listenerApi.getState().resLocation
			if (sameAsStart) {
				listenerApi.dispatch(
					updateResEditAddress({
						collectionAddressType: 'Same',
						collectionAddressId: id,
						collectionAddress: { ...address, latLong },
						collectionBranchId: address.branchId,
						deliveryAddressType: addressType,
						deliveryAddressId: id,
						deliveryAddress: { ...address, latLong },
						deliveryBranchId: address.branchId,
					})
				)
			} else {
				listenerApi.dispatch(
					updateResEditAddress({
						[`${type}AddressType`]: addressType,
						[`${type}Address`]: { ...address, latLong },
						[`${type}AddressId`]: id,
						[`${type}BranchId`]: address.branchId,
					})
				)
			}
		},
	})
}
addressListener(startAppListening)

export const addressPostcodeListener = (startListening: AppStartListening) => {
	startListening({
		actionCreator: updateAddress,
		effect: async (action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const { numDays, driverId, businessId } = selectApprovalProps(listenerApi.getState())
			const { type }: { type: 'collection' | 'delivery' } = action.payload
			let request: ApprovalReq = {
				fieldType: `${type === 'collection' ? 'Collection' : 'Delivery'}Type`,
				fieldValue: action.payload.businessId,
				businessId,
				rentalReservationId: null,
				alreadySetAddresses: false,
				driverId,
				numDays,
			}
			if (type === 'collection') {
				const collectionRequest = { ...request, collectionAddressType: action.payload.addressType }
				listenerApi.dispatch(fetchResApproval(collectionRequest))
			} else if (type === 'delivery') {
				const deliveryRequest = { ...request, deliveryAddressType: action.payload.addressType }
				listenerApi.dispatch(fetchResApproval(deliveryRequest))
			}
		},
	})
}
addressPostcodeListener(startAppListening)

/* approval listener ( listen for businessId change) */
export const businessListener = (startAppListening: AppStartListening) => {
	startAppListening({
		actionCreator: updateBusiness,
		effect: async (action, listenerApi) => {
			listenerApi.cancelActiveListeners()
			const state = listenerApi.getState()
			const { numDays, driverId } = selectApprovalProps(state)
			const { deliveryAddress, collectionAddress } = state.resLocation
			const { init } = state.resApproval
			if (action.payload?.businessId) {
				let request: ApprovalReq = {
					fieldType: 'Customer',
					fieldValue: action.payload.businessId,
					businessId: action.payload.businessId,
					rentalReservationId: null,
					alreadySetAddresses: false,
					driverId,
					numDays,
				}
				/* init just run business 'Customer' approval check */
				listenerApi.dispatch(fetchResApproval(request))
				if (!init) {
					request.fieldType = 'AddressPostcode'
					if (deliveryAddress.id !== null && deliveryAddress?.address?.postcode) {
						listenerApi.dispatch(
							fetchResApproval({
								...request,
								addressVal: deliveryAddress.address.postcode,
								addressType: 'delivery',
							})
						)
					}
					if (collectionAddress.id !== null && collectionAddress?.address?.postcode) {
						listenerApi.dispatch(
							fetchResApproval({
								...request,
								addressVal: collectionAddress.address.postcode,
							})
						)
					}
				}
			}
		},
	})
}
businessListener(startAppListening)
